import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, FormControlLabel, LinearProgress, Stack, Switch, Typography } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';

import CustomAxios from '../utils/CustomAxios';
import MuiButton from '../elements/Mui/mui_button';
import { blobErrorMessage, deliverPdf, filenameFromResponse, readBlobJson } from '../utils/downloadPdf';

export const SYNC_DOWNLOAD_LIMIT = 200;
export const POLL_INTERVAL_MS = 2000;
const ALL = 'All';

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

const formatAmount = (value) =>
  Number(value || 0).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export function DividendWarrant() {
  const [runs, setRuns] = useState([]);
  const [narration, setNarration] = useState(null);
  const [rows, setRows] = useState([]);
  const [released, setReleased] = useState(false);
  const [stateFilter, setStateFilter] = useState(ALL);
  const [estateFilter, setEstateFilter] = useState(ALL);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [downloading, setDownloading] = useState(null); // 'selected' | memberid
  const [job, setJob] = useState(null); // { taskId, done, total }
  const [releasing, setReleasing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const pollTimer = useRef(null);

  useEffect(() => {
    CustomAxios.get('oasis/dividend_warrant/runs/')
      .then((res) => setRuns(res.data.runs || []))
      .catch((err) => setError(errorMessage(err, 'Could not load dividend runs.')));
    return () => clearTimeout(pollTimer.current);
  }, []);

  const loadRun = async (value) => {
    setNarration(value);
    setRows([]);
    setSelected([]);
    setStateFilter(ALL);
    setEstateFilter(ALL);
    setSearch('');
    setError('');
    setNotice('');
    setLoadingRows(true);
    try {
      const res = await CustomAxios.post('oasis/dividend_warrant/list/', { narration: value });
      setRows(res.data.rows || []);
      setReleased(Boolean(res.data.released));
    } catch (err) {
      setError(errorMessage(err, 'Could not load warrants for this run.'));
    } finally {
      setLoadingRows(false);
    }
  };

  const states = useMemo(() => [ALL, ...new Set(rows.map((r) => r.state).filter(Boolean))], [rows]);
  const estates = useMemo(
    () => [ALL, ...new Set(rows.filter((r) => stateFilter === ALL || r.state === stateFilter).map((r) => r.estate).filter(Boolean))],
    [rows, stateFilter],
  );

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) =>
      (stateFilter === ALL || r.state === stateFilter) &&
      (estateFilter === ALL || r.estate === estateFilter) &&
      (!term || String(r.memberid).includes(term) || (r.name || '').toLowerCase().includes(term) || (r.nric || '').toLowerCase().includes(term)));
  }, [rows, stateFilter, estateFilter, search]);

  const download = async (memberids, key) => {
    setError('');
    setNotice('');
    if (memberids.length > SYNC_DOWNLOAD_LIMIT) {
      setError(`You can download up to ${SYNC_DOWNLOAD_LIMIT} warrants at once. Use "Generate all" for larger batches.`);
      return;
    }
    setDownloading(key);
    try {
      const res = await CustomAxios.post('oasis/dividend_warrant/download/', { narration, memberids }, { responseType: 'blob' });
      deliverPdf(res.data, filenameFromResponse(res, 'DividendWarrant.pdf'));
    } catch (err) {
      setError(await blobErrorMessage(err, 'Could not download the dividend warrant.'));
    } finally {
      setDownloading(null);
    }
  };

  const pollJob = async (taskId) => {
    try {
      const res = await CustomAxios.get(`oasis/dividend_warrant/task_result/${taskId}/`, { responseType: 'blob' });
      if (res.status === 202) {
        const progress = await readBlobJson(res.data);
        setJob({ taskId, done: progress.done, total: progress.total });
        pollTimer.current = setTimeout(() => pollJob(taskId), POLL_INTERVAL_MS);
        return;
      }
      deliverPdf(res.data, filenameFromResponse(res, 'DividendWarrant.pdf'));
      setNotice('All dividend warrants were generated.');
      setJob(null);
    } catch (err) {
      setError(await blobErrorMessage(err, 'Could not generate the dividend warrants.'));
      setJob(null);
    }
  };

  const generateAll = async () => {
    setError('');
    setNotice('');
    try {
      const res = await CustomAxios.post('oasis/dividend_warrant/bulk_start_task/', {
        narration,
        state: stateFilter === ALL ? null : stateFilter,
        estate: estateFilter === ALL ? null : estateFilter,
      });
      setJob({ taskId: res.data.task_id, done: 0, total: res.data.total });
      pollTimer.current = setTimeout(() => pollJob(res.data.task_id), POLL_INTERVAL_MS);
    } catch (err) {
      setError(errorMessage(err, 'Could not start generating the dividend warrants.'));
    }
  };

  const toggleRelease = async (event) => {
    const next = event.target.checked;
    setReleasing(true);
    setError('');
    setNotice('');
    try {
      const res = await CustomAxios.post('oasis/dividend_warrant/release/', { narration, released: next });
      setReleased(res.data.released);
      setRuns((current) => current.map((r) => (r.narration === narration ? { ...r, released: res.data.released } : r)));
      setNotice(res.data.released ? 'Members can now download this warrant in the Member Portal.' : 'This warrant is now hidden from the Member Portal.');
    } catch (err) {
      setError(errorMessage(err, 'Could not change Member Portal visibility.'));
    } finally {
      setReleasing(false);
    }
  };

  const busy = Boolean(downloading) || Boolean(job);
  // Rows ticked earlier but hidden by the current filters are not downloaded.
  const visibleIds = new Set(visibleRows.map((r) => r.memberid));
  const selectedIds = selected.map((r) => r.memberid).filter((id) => visibleIds.has(id));
  const bulkCount = rows.filter((r) => (stateFilter === ALL || r.state === stateFilter) && (estateFilter === ALL || r.estate === estateFilter)).length;

  const runOption = (run) => (
    <Stack direction="row" sx={{ gap: '8px', alignItems: 'center' }}>
      <span>{run.narration}</span>
      <Chip size="small" label={`${run.total} members`} />
      {run.released && <Chip size="small" color="success" label="In Member Portal" />}
    </Stack>
  );

  const nameBody = (row) => (
    <Stack direction="row" sx={{ gap: '8px', alignItems: 'center' }}>
      <span>{row.name}</span>
      {row.deceased && <Chip size="small" color="warning" label="Deceased" />}
    </Stack>
  );

  const pdfBody = (row) => (
    <MuiButton
      variant="outlined"
      value={downloading === row.memberid ? <CircularProgress size={18} aria-label="Downloading" /> : 'PDF'}
      disabled={busy}
      onClick={() => download([row.memberid], row.memberid)}
      btnsx={{ minWidth: '72px', width: '72px' }}
      typosx={{ fontSize: '14px' }}
    />
  );

  return (
    <Box sx={{ height: 'calc(100vh - 72px)', width: '100%', overflow: 'auto', marginTop: '60px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '0px 20px 20px 20px' }}>
        <Box sx={{ padding: '8px 0px', display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'space-between', borderBottom: '1px solid #CBD5E1', alignItems: 'center', minHeight: '84px' }}>
          <Stack direction="row" sx={{ gap: '12px', alignItems: 'center' }}>
            <ReceiptLongOutlinedIcon sx={{ width: '32px', height: '32px', color: '#288d2c' }} />
            <Typography sx={{ fontFamily: 'inter', fontSize: '20px', fontWeight: 600, lineHeight: '28px', color: '#101010' }}>Dividend Warrant</Typography>
          </Stack>
          <Dropdown
            inputId="dividend-run"
            value={narration}
            options={runs}
            optionLabel="narration"
            optionValue="narration"
            itemTemplate={runOption}
            onChange={(e) => loadRun(e.value)}
            placeholder="Select dividend run"
            aria-label="Dividend run"
            style={{ minWidth: '320px' }}
            disabled={busy}
          />
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}

        {narration && (
          <Stack direction="row" sx={{ gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <Dropdown inputId="warrant-state" value={stateFilter} options={states} onChange={(e) => { setStateFilter(e.value); setEstateFilter(ALL); }} aria-label="State" disabled={busy} />
            <Dropdown inputId="warrant-estate" value={estateFilter} options={estates} onChange={(e) => setEstateFilter(e.value)} aria-label="Estate" disabled={busy} filter />
            <span className="p-input-icon-left">
              <i className="pi pi-search" />
              <InputText id="warrant-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search Mno, name or MyKad" aria-label="Search" style={{ width: '280px' }} />
            </span>
            <Box sx={{ flexGrow: 1 }} />
            <FormControlLabel
              control={<Switch id="warrant-release" checked={released} onChange={toggleRelease} disabled={releasing || busy} color="success" />}
              label="Visible in Member Portal"
            />
            <MuiButton
              variant="contained"
              value={downloading === 'selected' ? <><CircularProgress size={18} color="inherit" /> Downloading…</> : `Download selected (${selectedIds.length})`}
              disabled={selectedIds.length === 0 || busy}
              onClick={() => download(selectedIds, 'selected')}
              btnsx={{ width: 'auto' }}
            />
            <MuiButton
              variant="outlined"
              value={`Generate all (${bulkCount})`}
              disabled={bulkCount === 0 || busy}
              onClick={generateAll}
              btnsx={{ width: 'auto' }}
            />
          </Stack>
        )}

        {job && (
          <Box role="status">
            <Typography sx={{ fontSize: '14px', color: '#475569', mb: '6px' }}>
              Generating {job.done.toLocaleString()} of {job.total.toLocaleString()} warrants…
            </Typography>
            <LinearProgress variant="determinate" value={job.total ? (job.done / job.total) * 100 : 0} color="success" />
          </Box>
        )}

        {narration && (
          <DataTable
            value={visibleRows}
            dataKey="memberid"
            loading={loadingRows}
            paginator
            rows={15}
            selectionMode="checkbox"
            selection={selected}
            onSelectionChange={(e) => setSelected(e.value)}
            emptyMessage="No dividend warrants match these filters."
            scrollable
            style={{ backgroundColor: '#F1F5F9' }}
          >
            <Column selectionMode="multiple" headerStyle={{ width: '48px' }} />
            <Column field="memberid" header="Mno" sortable />
            <Column field="name" header="Name" body={nameBody} sortable />
            <Column field="nric" header="MyKad" />
            <Column field="state" header="State" sortable />
            <Column field="estate" header="Estate" sortable />
            <Column field="total" header="Dividend (RM)" body={(r) => formatAmount(r.total)} style={{ textAlign: 'right' }} />
            <Column field="dws" header="DWS (RM)" body={(r) => formatAmount(r.dws)} style={{ textAlign: 'right' }} />
            <Column field="net" header="Net (RM)" body={(r) => formatAmount(r.net)} style={{ textAlign: 'right' }} />
            <Column field="payment_date" header="Payment Date" />
            <Column header="Warrant" body={pdfBody} />
          </DataTable>
        )}
      </Box>
    </Box>
  );
}
